import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MapPin, Mail, Link2, FileSignature, CheckCircle2, AlertTriangle, Circle, Plug, KeyRound, Send, Trash2, RefreshCw } from "lucide-react";

const STATUS = {
  connected: { tone: "bg-emerald-100 text-emerald-700", icon: CheckCircle2, label: "Connected" },
  error: { tone: "bg-red-100 text-red-700", icon: AlertTriangle, label: "Error" },
  not_configured: { tone: "bg-slate-100 text-slate-500", icon: Circle, label: "Not Configured" },
  external: { tone: "bg-blue-100 text-blue-700", icon: Link2, label: "Managed Separately" },
};

function StatusBadge({ status }) {
  const s = STATUS[status] || STATUS.not_configured;
  const I = s.icon;
  return <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${s.tone}`} data-testid="integration-status"><I className="w-3.5 h-3.5" />{s.label}</span>;
}

function IntegrationCard({ provider, title, icon: Icon, purpose, data, fields, onSave, onTest, onRemove, extra }) {
  const [form, setForm] = useState({});
  const [apiKey, setApiKey] = useState("");
  const [busy, setBusy] = useState("");
  useEffect(() => {
    const f = {};
    fields.forEach((fl) => { f[fl.k] = data[fl.k] ?? (fl.type === "bool" ? true : ""); });
    setForm(f);
  }, [data]); // eslint-disable-line

  const save = async () => {
    setBusy("save");
    await onSave({ ...form, ...(apiKey ? { api_key: apiKey } : {}) });
    setApiKey(""); setBusy("");
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm space-y-4" data-testid={`integration-card-${provider}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center text-navy"><Icon className="w-5 h-5" /></div>
          <div>
            <h3 className="font-heading font-semibold text-navy">{title}</h3>
            <p className="text-xs text-slate-500 max-w-md">{purpose}</p>
          </div>
        </div>
        <StatusBadge status={data.status} />
      </div>

      <div className="text-xs text-slate-400 flex flex-wrap gap-x-4 gap-y-1">
        {data.last_tested && <span>Last tested: {new Date(data.last_tested).toLocaleString("en-AU")}</span>}
        {data.last_test_result && <span>Result: {data.last_test_result}</span>}
        {data.configured_by && <span>By: {data.configured_by}</span>}
      </div>

      <div>
        <Label className="text-xs">API Key</Label>
        <div className="flex gap-2 mt-1">
          <Input type="password" value={apiKey} onChange={(e) => setApiKey(e.target.value)} data-testid={`${provider}-api-key`}
            placeholder={data.custom_key ? data.key_masked : (data.has_key ? "Using environment default — enter to override" : "Enter API key")} />
        </div>
        {data.custom_key && <p className="text-xs text-slate-400 mt-1">Saved key: {data.key_masked} (enter a new value to replace)</p>}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {fields.map((fl) => (
          <div key={fl.k} className={fl.type === "bool" ? "flex items-center gap-2" : ""}>
            {fl.type === "bool" ? (
              <label className="flex items-center gap-2 text-sm text-slate-600">
                <input type="checkbox" checked={!!form[fl.k]} onChange={(e) => setForm({ ...form, [fl.k]: e.target.checked })} data-testid={`${provider}-${fl.k}`} />{fl.label}
              </label>
            ) : (
              <>
                <Label className="text-xs">{fl.label}</Label>
                <Input value={form[fl.k] || ""} onChange={(e) => setForm({ ...form, [fl.k]: e.target.value })} className="mt-1" data-testid={`${provider}-${fl.k}`} />
              </>
            )}
          </div>
        ))}
      </div>

      {extra && extra(form)}

      <div className="flex flex-wrap gap-2 pt-1">
        <Button onClick={save} disabled={busy === "save"} className="bg-navy hover:bg-navy/90" data-testid={`${provider}-save`}><KeyRound className="w-4 h-4 mr-2" />Save Securely</Button>
        <Button variant="outline" onClick={() => { setBusy("test"); onTest().finally(() => setBusy("")); }} disabled={busy === "test" || !data.has_key} data-testid={`${provider}-test`}><RefreshCw className="w-4 h-4 mr-2" />{busy === "test" ? "Testing…" : "Test Connection"}</Button>
        {data.custom_key && <Button variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={onRemove} data-testid={`${provider}-remove`}><Trash2 className="w-4 h-4 mr-2" />Remove Key</Button>}
      </div>
    </div>
  );
}

export default function Integrations() {
  const [data, setData] = useState(null);
  const [testTo, setTestTo] = useState("");
  const load = () => api.get("/settings/integrations").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);
  if (!data) return <div className="text-slate-400">Loading…</div>;

  const save = (provider) => async (payload) => {
    try { await api.put(`/settings/integrations/${provider}`, payload); toast.success("Saved securely"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const test = (provider) => async () => {
    try { const r = await api.post(`/settings/integrations/${provider}/test`); r.data.status === "connected" ? toast.success(r.data.message) : toast.error(r.data.message); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Test failed"); }
  };
  const remove = (provider) => async () => {
    if (!window.confirm("Remove this API key? The app will fall back to the environment default (if any).")) return;
    try { await api.delete(`/settings/integrations/${provider}`); toast.success("Credentials removed"); load(); }
    catch (e) { toast.error("Remove failed"); }
  };
  const sendTest = async () => {
    if (!testTo.trim()) { toast.error("Enter a recipient email"); return; }
    try { const r = await api.post("/settings/integrations/resend/test-email", { to: testTo }); toast.success(r.data.message); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Send failed"); }
  };

  return (
    <div className="space-y-6 animate-fade-up max-w-5xl" data-testid="integrations-page">
      <div>
        <h1 className="text-3xl sm:text-4xl font-heading font-bold text-navy flex items-center gap-2"><Plug className="w-7 h-7" />Integrations</h1>
        <p className="text-slate-500 mt-1">Connect your own API keys — stored encrypted, never shown in full again.</p>
      </div>

      <IntegrationCard provider="google_maps" title="Google Maps" icon={MapPin}
        purpose="Address search, validation, maps & distance using your own Google credentials."
        data={data.google_maps} onSave={save("google_maps")} onTest={test("google_maps")} onRemove={remove("google_maps")}
        fields={[
          { k: "allowed_domain", label: "Allowed Website Domain" },
          { k: "enable_autocomplete", label: "Enable Address Autocomplete", type: "bool" },
          { k: "enable_maps", label: "Enable Maps", type: "bool" },
          { k: "enable_distance", label: "Enable Distance Calculations", type: "bool" },
        ]} />

      <IntegrationCard provider="resend" title="Resend Email" icon={Mail}
        purpose="Send invoices, reminders & summaries from your own verified sender domain."
        data={data.resend} onSave={save("resend")} onTest={test("resend")} onRemove={remove("resend")}
        fields={[
          { k: "from_name", label: "Default From Name" },
          { k: "from_email", label: "Default From Email" },
          { k: "reply_to_name", label: "Reply-To Name" },
          { k: "reply_to_email", label: "Reply-To Email" },
          { k: "sending_domain", label: "Sending Domain" },
          { k: "accounts_email", label: "Accounts Email (optional)" },
          { k: "cc", label: "Default CC (optional)" },
          { k: "bcc", label: "Office BCC — copies every email (blank = business email)" },
        ]}
        extra={() => (
          <div className="space-y-3">
            <div className="rounded-xl border border-emerald-100 bg-emerald-50 p-3" data-testid="resend-bcc-note">
              <p className="text-xs text-emerald-800">
                <span className="font-semibold">Office copy is ON.</span> Every outgoing email (job confirmations, invoices, reminders, signing requests & onboarding) is silently BCC'd to <span className="font-semibold">{data.resend?.effective_bcc || "info@truesealcaulking.com.au"}</span> so you keep a record in your inbox. Change it in the “Office BCC” field above, or leave blank to use the business email.
              </p>
            </div>
            <div className="rounded-xl border border-slate-100 bg-slate-50 p-3">
              <Label className="text-xs">Send Test Email</Label>
              <div className="flex gap-2 mt-1">
                <Input value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="recipient@example.com" data-testid="resend-test-to" />
                <Button variant="outline" onClick={sendTest} data-testid="resend-test-email-btn"><Send className="w-4 h-4 mr-2" />Send Test</Button>
              </div>
            </div>
          </div>
        )} />

      {/* Xero & DocuSign status cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm flex items-center justify-between" data-testid="integration-card-xero">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center text-blue-600"><Link2 className="w-5 h-5" /></div>
            <div><h3 className="font-heading font-semibold text-navy">Xero</h3><p className="text-xs text-slate-500">Accounting & invoice sync</p></div>
          </div>
          <Link to="/settings/xero" className="text-sm text-blue-700 font-medium hover:underline">Manage →</Link>
        </div>
        <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm flex items-center justify-between opacity-70" data-testid="integration-card-docusign">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center text-slate-500"><FileSignature className="w-5 h-5" /></div>
            <div><h3 className="font-heading font-semibold text-navy">DocuSign</h3><p className="text-xs text-slate-500">E-signatures</p></div>
          </div>
          <StatusBadge status="not_configured" />
        </div>
      </div>
    </div>
  );
}
